/**
 * CartContext.jsx
 *
 * Global cart + auth state shared across ALL pages.
 *
 * What it provides:
 *   Cart:  items, totalCount, subtotal, isInCart, getItem,
 *          addItem, setQty, removeItem, clearCart
 *   Auth:  currentUser, isLoggedIn, login(user), logout()
 *
 * Auth helpers:
 *   login(user)  — saves user to localStorage + state
 *   logout()     — clears user from localStorage + state (cart kept)
 *   isLoggedIn   — boolean derived from currentUser
 *
 * Wrap your entire app once in App.js:
 *   <CartProvider> … </CartProvider>
 */

import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
  } from "react";
  
  // ── Storage keys ──────────────────────────────────────────────────────────────
  
  const CART_KEY = "cartItems";
  const USER_KEY = "currentUser";
  
  // ── Storage helpers ───────────────────────────────────────────────────────────
  
  const readCart = () => {
    try { return JSON.parse(localStorage.getItem(CART_KEY) || "[]"); }
    catch { return []; }
  };
  
  const writeCart = (items) =>
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  
  const readUser = () => {
    try {
      return (
        JSON.parse(localStorage.getItem(USER_KEY)) ||
        JSON.parse(localStorage.getItem("user")) ||
        null
      );
    } catch { return null; }
  };
  
  // ── Context ───────────────────────────────────────────────────────────────────
  
  const CartContext = createContext(null);
  
  // ── Provider ──────────────────────────────────────────────────────────────────
  
  export const CartProvider = ({ children }) => {
    // ── Cart state ─────────────────────────────────────────────────────────────
    const [items, setItems] = useState(() => readCart());
  
    // ── Auth state ─────────────────────────────────────────────────────────────
    const [currentUser, setCurrentUser] = useState(() => readUser());
  
    // Persist cart to localStorage on every change
    useEffect(() => { writeCart(items); }, [items]);
  
    // Sync cart across tabs
    useEffect(() => {
      const onStorage = (e) => {
        if (e.key === CART_KEY) setItems(readCart());
        if (e.key === USER_KEY || e.key === "user") setCurrentUser(readUser());
      };
      window.addEventListener("storage", onStorage);
      return () => window.removeEventListener("storage", onStorage);
    }, []);

    // Sync cart within the same tab (SummaryPage clears via clearLocalCart which fires "cart-updated")
    useEffect(() => {
      const onCartUpdated = () => setItems(readCart());
      window.addEventListener("cart-updated", onCartUpdated);
      return () => window.removeEventListener("cart-updated", onCartUpdated);
    }, []);
  
    // ── Auth mutations ─────────────────────────────────────────────────────────
  
    /**
     * Call after a successful login API response.
     * user — the user object returned by your backend.
     */
    const login = useCallback((user) => {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      setCurrentUser(user);
    }, []);
  
    /**
     * Call on logout. Cart is intentionally preserved so a
     * guest session's items aren't lost if the user logs back in.
     */
    const logout = useCallback(() => {
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem("user");
      localStorage.removeItem("authToken");
      localStorage.removeItem("access_token");
      localStorage.removeItem("token");
      localStorage.removeItem("tempCart"); // clean up old key
      setCurrentUser(null);
    }, []);
  
    const isLoggedIn = Boolean(currentUser);
  
    // ── Cart mutations ─────────────────────────────────────────────────────────
  
    const addItem = useCallback((service) => {
      setItems((prev) => {
        const existing = prev.find((i) => i.id === service.id);
        if (existing) {
          return prev.map((i) =>
            i.id === service.id ? { ...i, qty: (i.qty || 1) + 1 } : i
          );
        }
        return [
          ...prev,
          {
            id: service.id,
            title: service.title || service.name || service.service_name || "Service",
            subtitle: service.subtitle || service.category || "",
            price: Number(service.price || service.amount || 0),
            image: service.image || service.service_image || null,
            qty: 1,
            addedAt: Date.now(),
          },
        ];
      });
    }, []);
  
    const setQty = useCallback((id, qty) => {
      if (qty <= 0) {
        setItems((prev) => prev.filter((i) => i.id !== id));
      } else {
        setItems((prev) => prev.map((i) => (i.id === id ? { ...i, qty } : i)));
      }
    }, []);
  
    const removeItem = useCallback((id) => {
      setItems((prev) => prev.filter((i) => i.id !== id));
    }, []);
  
    const clearCart = useCallback(() => {
      setItems([]);
      localStorage.removeItem(CART_KEY);
    }, []);
  
    // ── Derived cart values ────────────────────────────────────────────────────
  
    const totalCount = useMemo(
      () => items.reduce((n, i) => n + (i.qty || 1), 0),
      [items]
    );
  
    const subtotal = useMemo(
      () => items.reduce((s, i) => s + Number(i.price || 0) * Number(i.qty || 1), 0),
      [items]
    );
  
    const isInCart = useCallback((id) => items.some((i) => i.id === id), [items]);
    const getItem  = useCallback((id) => items.find((i) => i.id === id) || null, [items]);
  
    return (
      <CartContext.Provider
        value={{
          // Cart
          items,
          totalCount,
          subtotal,
          isInCart,
          getItem,
          addItem,
          setQty,
          removeItem,
          clearCart,
          // Auth
          currentUser,
          isLoggedIn,
          login,
          logout,
        }}
      >
        {children}
      </CartContext.Provider>
    );
  };
  
  // ── Hook ──────────────────────────────────────────────────────────────────────
  
  export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
    return ctx;
  };
  
  export default CartContext;